import React, { useCallback, useEffect, useState } from 'react';
import RecoveryResetPanel from '../securityRecovery/RecoveryResetPanel';
import { getOperationsCsrf } from './adminApi';
import './OwnerSecurity.css';

const BASE = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/$/, '');
async function api(url, method = 'GET', payload) {
  const headers = {};
  if (method !== 'GET') {
    const csrf = await getOperationsCsrf();
    headers['X-CSRFToken'] = csrf.csrfToken;
    headers['Content-Type'] = 'application/json';
  }
  const response = await fetch(`${BASE}${url}`, {method, credentials: 'include', headers,
    ...(payload ? {body: JSON.stringify(payload)} : {}), cache: 'no-store'});
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const detail = data.detail || Object.entries(data).map(([key, value]) => `${key}: ${Array.isArray(value) ? value.join(', ') : value}`).join('; ');
    throw new Error(detail || `Request failed (${response.status})`);
  }
  return data;
}
export default function OwnerSecurity({ restaurantId, role }) {
  const [staff, setStaff] = useState([]);
  const [selected, setSelected] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [actorPassword, setActorPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [confirmAction, setConfirmAction] = useState(false);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api(`/api/operations/${encodeURIComponent(restaurantId)}/admin/staff/`);
      setStaff((data.staff || []).filter(x => ['MANAGER','CASHIER','KITCHEN','WAITER'].includes(x.role) && x.is_active));
      setError('');
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  }, [restaurantId]);
  useEffect(() => { load(); }, [load]);
  async function submit(e) {
    e.preventDefault(); setError(''); setNotice('');
    if (password !== confirmPassword) { setError('New passwords do not match.'); return; }
    if (!/^\d{6}$/.test(otp)) { setError('Enter a valid 6-digit authenticator code.'); return; }
    if (!confirmAction) { setError('Confirm the account security action first.'); return; }
    setBusy(true);
    try {
      const result = await api(`/api/operations/${encodeURIComponent(restaurantId)}/admin/staff/${encodeURIComponent(selected)}/password/`, 'POST',
        {password, actor_password: actorPassword, actor_otp: otp});
      setNotice(result.detail || 'Staff password reset completed.');
      setPassword(''); setConfirmPassword(''); setActorPassword(''); setOtp(''); setSelected(''); setConfirmAction(false);
    } catch (err) { setError(err.message); }
    finally { setBusy(false); }
  }
  if (role !== 'OWNER') return <div className="os-card">Only the restaurant Owner can manage account security.</div>;
  return <div className="os-page">
    <div className="os-intro"><span className="os-eyebrow">FOODHORIZON · ACCOUNT SECURITY</span><h2>Owner Security Management</h2><p>Securely manage your team's login credentials and Manager authenticator recovery.</p></div>
    <section className="os-card">
      <div className="os-card-head"><span className="os-symbol">🔐</span><div><h3>Staff password management</h3><p>Reset a staff password with your Owner password and a fresh authenticator code.</p></div></div>
      <div className="os-alert">Restricted action · The selected staff member's existing database sessions will be revoked after a successful reset.</div>
      {error && <div className="os-error" role="alert">{error}</div>}
      {notice && <div className="os-success" role="status">{notice}</div>}
      <form onSubmit={submit} className="os-form">
        <label className="os-wide">Staff member<select required value={selected} disabled={busy || loading} onChange={e => setSelected(e.target.value)}><option value="">{loading ? 'Loading staff…' : 'Select a Manager, Cashier, Kitchen, or Waiter'}</option>{staff.map(x => <option key={x.id} value={x.id}>{[x.first_name,x.last_name].filter(Boolean).join(' ') || x.username} · {x.role} (@{x.username})</option>)}</select></label>
        <label>New password<input required type="password" autoComplete="new-password" minLength={8} value={password} disabled={busy} onChange={e => setPassword(e.target.value)} /></label>
        <label>Confirm new password<input required type="password" autoComplete="new-password" minLength={8} value={confirmPassword} disabled={busy} onChange={e => setConfirmPassword(e.target.value)} /></label>
        <label>Owner account password<input required type="password" autoComplete="current-password" value={actorPassword} disabled={busy} onChange={e => setActorPassword(e.target.value)} /></label>
        <label>Fresh authenticator OTP<input required inputMode="numeric" pattern="[0-9]{6}" maxLength={6} placeholder="6-digit code" autoComplete="one-time-code" value={otp} disabled={busy} onChange={e => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))} /></label>
        <label className="os-check os-wide"><input type="checkbox" checked={confirmAction} disabled={busy} onChange={e => setConfirmAction(e.target.checked)} /> I confirm that I am authorized to reset this staff member's password.</label>
        <div className="os-actions os-wide"><button type="submit" disabled={busy || !selected || !confirmAction}>{busy ? 'Verifying and resetting…' : 'Reset staff password'}</button><button type="button" className="os-secondary" disabled={busy} onClick={() => {setPassword('');setConfirmPassword('');setActorPassword('');setOtp('');setConfirmAction(false);setError('');setNotice('');}}>Clear form</button><button type="button" className="os-secondary" disabled={busy} onClick={load}>Refresh staff</button></div>
      </form>
    </section>
    <RecoveryResetPanel mode="owner" restaurantSlug={restaurantId} />
  </div>;
}
